import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { 
  Play, BookOpen, ChevronRight, 
  Layers, Paintbrush, Hammer, Ruler,
  Globe, MonitorPlay, ShieldCheck, CheckCircle2,
  BrainCircuit, X, Trophy, Smartphone, Download, 
  HelpCircle, Mail, MapPin, Send, Medal,
  Lock, Share2, FileText, Star, Quote, ArrowRight,
  CreditCard, RefreshCcw, Target, Briefcase, TrendingUp, Zap, Check, Unlock, Users,
  Menu, Phone
} from 'lucide-react';
import { Navbar } from '../components/Layout';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { useAuth } from '../context/AuthContext';
import AdmissionCounter from '../components/AdmissionCounter';
import HeroWithCourses from '../components/HeroWithCourses';
import HomePricingCard from '../components/HomePricingCard';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

// Minimalist, fast animations
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

// Verified High-Res Assets
const IMAGES = {
  heroDraft: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=800",
  heroWood: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?q=80&w=800",
  heroFinish: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=800",
  messy: "https://images.unsplash.com/photo-1581287053822-fd7bf4f4bfec?q=80&w=800",
  clean: "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=800",
  sequence: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?q=80&w=1200",
  preview: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1200",
  certificate: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=1200",
  dashboard: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1200", 
  guild: [ 
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=800",
    "https://images.unsplash.com/photo-1534398079543-7ae6d016b86a?q=80&w=800",
    "https://images.unsplash.com/photo-1505691938895-1758d7feb511?q=80&w=800",
    "https://images.unsplash.com/photo-1581094794329-c8112a89af12?q=80&w=800"
  ]
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [activeFaq, setActiveFaq] = useState(null);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pricingSettings, setPricingSettings] = useState(null);

  // Track WhatsApp share-link clicks (Share & Earn)
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) {
      axios.post(`${API_URL}/share/track`, null, { params: { ref } }).catch(() => {});
    }
  }, []);

  // Lifetime plan pricing (admin-configurable, shown in the pricing grid below)
  useEffect(() => {
    axios.get(`${API_URL}/settings`).then((res) => {
      setPricingSettings(res.data || null);
    }).catch(() => {});
  }, []);

  // Auto-Popup & Exit Intent Logic
  useEffect(() => {
    const autoTimer = setTimeout(() => {
      const hasSeenPopup = localStorage.getItem('hasSeenExitPopup');
      if (!hasSeenPopup) {
        setShowExitPopup(true);
        localStorage.setItem('hasSeenExitPopup', 'true');
      }
    }, 15000);

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 || e.clientX <= 0 || (e.clientX >= window.innerWidth || e.clientY >= window.innerHeight)) {
        const hasSeenPopup = localStorage.getItem('hasSeenExitPopup');
        if (!hasSeenPopup) {
          setShowExitPopup(true);
          localStorage.setItem('hasSeenExitPopup', 'true');
        }
      }
    };
    
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(autoTimer);
    };
  }, []);

  // Smooth Scroll handler for anchor links
  const handleScroll = (e, id) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Account for sticky header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const faqList = [
    { q: "What is the device limit?", a: "To prevent account sharing, subscriptions are strictly limited to 3 active devices. You can manage devices directly from your dashboard." },
    { q: "Do I get physical materials?", a: "No, Thachari is a digital platform. We provide downloadable blueprints, cutting lists, and comprehensive video guidance." },
    { q: "Are the certificates accredited?", a: "Our PDF certificates are recognized by leading industry partners. They serve as verifiable proof of your completed coursework and approved project reviews." },
    { q: "What payment methods are accepted?", a: "We securely accept all major credit cards via Stripe, as well as PayPal, for all subscription plans." },
    { q: "Can I download videos for offline viewing?", a: "Yes. By downloading our native mobile applications, you can securely cache 4K videos for offline viewing in your workshop." },
    { q: "How does the Gemini AI Assistant work?", a: "Our integrated AI acts as your personal coach—instantly generating spatial layouts and chatting through complex carpentry problems." },
    { q: "Can I cancel my subscription at any time?", a: "Absolutely. You can manage, pause, or cancel your subscription instantly from your billing settings." }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] dark:bg-[#050505] text-[#2D2D2D] dark:text-[#F3F4F6] font-sans selection:bg-[#A67C52] selection:text-white overflow-hidden pb-16 md:pb-0">
      <SEO
        title="Interior Technician & Carpentry Course Online"
        description="Learn interior site execution, measurement, cutting list, and material calculation. Professional interior technician training online. India's best interior construction course."
        keywords="interior technician course, interior technician course India, interior technical course, interior site execution course, interior site supervisor course, interior construction course, interior fit out course, interior measurement course, interior cutting list course, furniture cutting list course, interior material course, interior quotation course, interior costing course"
        url="/"
      />

      {/* POPUP / MODAL */}
      <AnimatePresence>
        {showExitPopup && (
          <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-6 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white dark:bg-[#111] p-10 md:p-14 max-w-xl w-full relative shadow-2xl border-t-4 border-[#A67C52]">
              <button onClick={() => setShowExitPopup(false)} className="absolute top-4 right-4 text-gray-500 hover:text-black dark:hover:text-white transition-colors"><X className="w-6 h-6"/></button>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-[#2D2D2D] dark:text-white leading-tight">Wait! Watch a free lesson before you go.</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg font-medium">See the exact step-by-step methods our students use to land ₹25,000+ interior projects and custom furniture orders.</p>
              <Button
                onClick={() => { window.location.href = 'https://thachari.com/courses' }}
                onTouchStart={() => { window.location.href = 'https://thachari.com/courses' }}
                className="w-full h-16 bg-[#A67C52] hover:bg-[#8B6640] text-white text-xl font-bold rounded-none shadow-xl"
              >
                Start Free Lesson Now <Play className="ml-2 w-5 h-5 fill-current"/>
              </Button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* CUSTOM LANDING PAGE HEADER WITH HYBRID NAVIGATION */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 dark:bg-[#050505]/90 backdrop-blur-xl border-b border-gray-200 dark:border-white/10 transition-colors duration-300">
        {pricingSettings?.maintenance_mode && (
          <div className="w-full bg-amber-500 text-white text-xs sm:text-sm font-medium text-center py-1.5 px-4" data-testid="maintenance-banner">
            🔧 Scheduled Maintenance — Login temporarily unavailable
          </div>
        )}
        <div className="max-w-[1600px] mx-auto px-6 h-20 flex items-center justify-between">
          
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <div className="w-10 h-10 bg-[#A67C52] text-white flex items-center justify-center font-serif font-bold text-2xl rounded-sm shadow-md">T</div>
            <span className="font-serif font-bold text-2xl tracking-widest text-[#2D2D2D] dark:text-white hidden sm:block">THACHARI</span>
          </div>

          {/* Desktop Navigation (Courses, Live + Anchors) */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8 font-bold text-sm text-gray-600 dark:text-gray-300 uppercase tracking-wider">
            <Link to="/courses" className="hover:text-[#A67C52] transition-colors">Courses</Link>
            <Link to="/live-classes" className="hover:text-[#A67C52] transition-colors">Live</Link>
            <a href="#methodology" onClick={(e) => handleScroll(e, 'methodology')} className="hover:text-[#A67C52] transition-colors">Methodology</a>
            <a href="#pricing" onClick={(e) => handleScroll(e, 'pricing')} className="hover:text-[#A67C52] transition-colors">Pricing</a>
            <a href="#faq" onClick={(e) => handleScroll(e, 'faq')} className="hover:text-[#A67C52] transition-colors">FAQ</a>
            <a href="#contact" onClick={(e) => handleScroll(e, 'contact')} className="hover:text-[#A67C52] transition-colors">Contact</a>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <Link to={isAdmin ? '/admin' : '/dashboard'} className="hidden sm:block text-sm font-bold text-[#2D2D2D] dark:text-white hover:text-[#A67C52] transition-colors">
                  {isAdmin ? 'Admin Panel' : 'Dashboard'}
                </Link>
                <Button onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')} className="bg-[#A67C52] hover:bg-[#8B6640] text-white rounded-md px-6 font-bold text-sm shadow-md hidden sm:flex">
                  Go to {isAdmin ? 'Admin' : 'Dashboard'}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block text-sm font-bold text-[#2D2D2D] dark:text-white hover:text-[#A67C52] transition-colors">Sign In</Link>
                <Button
                  onClick={() => { window.location.href = 'https://thachari.com/signup' }}
                  onTouchStart={() => { window.location.href = 'https://thachari.com/signup' }}
                  className="bg-[#A67C52] hover:bg-[#8B6640] text-white rounded-md px-6 font-bold text-sm shadow-md hidden sm:flex"
                >
                  Get Started
                </Button>
              </>
            )}
            {/* Mobile Menu Toggle */}
            <button className="lg:hidden text-[#2D2D2D] dark:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }} 
              animate={{ height: 'auto', opacity: 1 }} 
              exit={{ height: 0, opacity: 0 }} 
              className="lg:hidden bg-[#FDFBF7] dark:bg-[#0A0A0A] border-b border-gray-200 dark:border-white/10 overflow-hidden shadow-xl"
            >
              <div className="flex flex-col px-6 py-4 space-y-4 font-bold text-lg text-[#2D2D2D] dark:text-white">
                <Link to="/courses" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#A67C52]">Courses</Link>
                <Link to="/live-classes" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#A67C52]">Live Classes</Link>
                <a href="#methodology" onClick={(e) => handleScroll(e, 'methodology')} className="hover:text-[#A67C52]">Methodology</a>
                <a href="#pricing" onClick={(e) => handleScroll(e, 'pricing')} className="hover:text-[#A67C52]">Pricing</a>
                <a href="#faq" onClick={(e) => handleScroll(e, 'faq')} className="hover:text-[#A67C52]">FAQ</a>
                <a href="#contact" onClick={(e) => handleScroll(e, 'contact')} className="hover:text-[#A67C52]">Contact Us</a>
                <div className="pt-4 border-t border-gray-200 dark:border-white/10 flex flex-col gap-4">
                  {user ? (
                    <>
                      <Link to={isAdmin ? '/admin' : '/dashboard'} onClick={() => setMobileMenuOpen(false)} className="hover:text-[#A67C52]">
                        {isAdmin ? 'Admin Panel' : 'Dashboard'}
                      </Link>
                      <Button onClick={() => { setMobileMenuOpen(false); navigate(isAdmin ? '/admin' : '/dashboard'); }} className="bg-[#A67C52] hover:bg-[#8B6640] text-white w-full h-12 text-lg">
                        Go to {isAdmin ? 'Admin' : 'Dashboard'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#A67C52]">Sign In</Link>
                      <Button
                        onClick={() => { window.location.href = 'https://thachari.com/signup' }}
                        onTouchStart={() => { window.location.href = 'https://thachari.com/signup' }}
                        className="bg-[#A67C52] hover:bg-[#8B6640] text-white w-full h-12 text-lg"
                      >Get Started</Button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* 1. HERO - FIXED OVERLAP & PADDING */}
      <section id="hero" className="relative min-h-[calc(100vh-80px)] flex items-center overflow-hidden bg-[#FDFBF7] dark:bg-[#050505] pt-24 pb-16 lg:pt-32 lg:pb-24 mt-20 lg:mt-0">
        <div className="max-w-[1600px] w-full mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-8 items-center relative z-20">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-2xl">
            <motion.h1 variants={fadeUp} className="text-5xl md:text-6xl font-serif font-bold leading-[1.05] tracking-tight mb-6 text-[#2D2D2D] dark:text-white">
              Learn Interior Design & Technical Carpentry — <br className="hidden md:block"/>
              <span className="text-[#A67C52]">From Beginner to Professional</span>
            </motion.h1>
            
            <motion.p variants={fadeUp} className="text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-6 font-medium leading-relaxed">
              Step-by-step video training, real projects, certificates, and AI tools — all in one platform.
            </motion.p>
            
            <motion.div variants={fadeUp} className="space-y-3 mb-10 text-base lg:text-lg font-medium text-gray-700 dark:text-gray-300">
              <p className="mb-4">Become a skilled interior designer or carpenter — <span className="font-bold underline decoration-[#A67C52] decoration-2">even if you're starting from zero.</span></p>
              <div className="flex items-center"><CheckCircle2 className="w-5 h-5 mr-3 text-[#A67C52] shrink-0"/> Step-by-step video training</div>
              <div className="flex items-center"><CheckCircle2 className="w-5 h-5 mr-3 text-[#A67C52] shrink-0"/> Real-world portfolio projects</div>
              <div className="flex items-center"><CheckCircle2 className="w-5 h-5 mr-3 text-[#A67C52] shrink-0"/> Industry-recognized certificates</div>
              <div className="flex items-center pt-2 text-[#A67C52] font-bold"><MapPin className="w-5 h-5 mr-3 shrink-0"/> Works for Indian homes, materials & budgets</div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex flex-col w-full sm:w-auto">
                <Button
                  onClick={() => { window.location.href = user ? 'https://thachari.com/courses' : 'https://thachari.com/signup' }}
                  onTouchStart={() => { window.location.href = user ? 'https://thachari.com/courses' : 'https://thachari.com/signup' }}
                  className="w-full sm:w-auto h-14 lg:h-16 px-8 lg:px-10 text-base lg:text-lg font-bold bg-[#A67C52] text-white hover:bg-[#8B6640] rounded-none flex items-center justify-center transition-colors shadow-[0_10px_30px_rgba(166,124,82,0.3)]"
                >
                  Start Learning Now <ArrowRight className="ml-3 w-5 h-5"/>
                </Button>
                <div className="mt-4 text-gray-500 dark:text-gray-400 font-bold flex flex-col items-center sm:items-start gap-1">
                  <span className="flex items-center text-sm md:text-base"><Unlock className="w-4 h-4 mr-2 shrink-0"/> Watch your first lesson instantly. <span className="text-[#A67C52] ml-1">Start learning in under 30 seconds.</span></span>
                  <span className="text-xs md:text-sm bg-gray-200 dark:bg-white/10 px-3 py-1 mt-1 text-[#2D2D2D] dark:text-white uppercase tracking-wider">Start with: Advanced Joinery Masterclass</span>
                </div>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-2 mt-5 text-sm text-gray-600 dark:text-gray-400">
              <span>Questions? Call or WhatsApp:</span>
              <a href="https://wa.me/919626722220" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-bold text-[#25D366]">
                <WhatsAppIcon className="w-4 h-4" /> +91 96267 22220
              </a>
            </motion.div>
          </motion.div>

          {/* Course showcase — right column on desktop, below the headline/CTA on mobile.
              Falls back to the original image collage if courses can't load. */}
          <div className="relative w-full max-w-2xl lg:max-w-none mx-auto">
            <HeroWithCourses
              fallback={
              <div className="relative h-[400px] lg:h-[600px] hidden lg:block perspective-[2000px]">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-[#A67C52]/5 dark:bg-[#A67C52]/10 rounded-full blur-[100px]" />
                <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }} className="absolute top-[10%] left-[10%] w-48 h-64 xl:w-56 xl:h-72 bg-white p-2 border border-gray-100 dark:border-white/10 shadow-sm z-10">
                  <img src={IMAGES.heroDraft} className="w-full h-full object-cover grayscale opacity-80" alt="Draft" />
                </motion.div>
                <motion.div animate={{ y: [0, 15, 0] }} transition={{ repeat: Infinity, duration: 8, ease: "easeInOut", delay: 1 }} className="absolute top-[35%] right-[5%] w-56 h-80 xl:w-64 xl:h-80 bg-white p-2 border border-gray-100 dark:border-white/10 shadow-sm z-30">
                  <img src={IMAGES.heroWood} className="w-full h-full object-cover" alt="Wood" />
                </motion.div>
                <motion.div animate={{ y: [-5, 10, -5] }} transition={{ repeat: Infinity, duration: 7, ease: "easeInOut", delay: 0.5 }} className="absolute top-[20%] left-[30%] w-64 h-[350px] xl:w-72 xl:h-[400px] bg-white p-2 border border-gray-200 dark:border-white/20 shadow-md z-20 flex flex-col">
                  <img src={IMAGES.heroFinish} className="w-full flex-grow object-cover" alt="Finish" />
                  <div className="bg-white/90 p-3 border-t border-gray-100 flex items-center justify-between">
                    <div><p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Skill Acquired</p><p className="text-sm font-serif font-bold text-black">Commercial Zoning</p></div>
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                </motion.div>
              </div>
              }
            />
          </div>
        </div>
      </section>

      {/* 2. TRUST LOGOS */}
      <section className="py-10 border-y border-gray-200 dark:border-white/5 bg-white/50 dark:bg-[#0A0A0A]/50">
        <div className="max-w-[1400px] mx-auto px-6 text-center opacity-60 dark:opacity-40 grayscale">
          <p className="text-xs font-bold tracking-[0.2em] uppercase mb-8">Trusted by 10,000+ learners & industry professionals worldwide</p>
          <div className="flex justify-center gap-10 md:gap-24 flex-wrap items-center">
             <span className="font-serif text-2xl font-bold">StudioArc</span>
             <span className="font-sans text-xl font-bold tracking-tight">Lumina<span className="font-light">Design</span></span>
             <span className="font-serif text-2xl font-medium italic">Craftsman Guild</span>
             <span className="font-sans text-xl font-bold uppercase tracking-widest">Aura</span>
          </div>
        </div>
      </section>

      {/* 3. WHO IS THIS FOR? */}
      <section id="audience" className="py-24 bg-[#FDFBF7] dark:bg-[#050505]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-[#2D2D2D] dark:text-white">Who Is This For?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
             <div className="flex items-center gap-4 bg-white dark:bg-[#111] p-6 border border-gray-200 dark:border-white/5 rounded-[1.5rem] shadow-sm">
               <Target className="w-8 h-8 text-[#A67C52] shrink-0"/>
               <p className="font-bold text-lg leading-tight">Beginners starting from zero</p>
             </div>
             <div className="flex items-center gap-4 bg-white dark:bg-[#111] p-6 border border-gray-200 dark:border-white/5 rounded-[1.5rem] shadow-sm">
               <Ruler className="w-8 h-8 text-[#A67C52] shrink-0"/>
               <p className="font-bold text-lg leading-tight">Interior designers upgrading skills</p>
             </div>
             <div className="flex items-center gap-4 bg-white dark:bg-[#111] p-6 border border-gray-200 dark:border-white/5 rounded-[1.5rem] shadow-sm">
               <Hammer className="w-8 h-8 text-[#A67C52] shrink-0"/>
               <p className="font-bold text-lg leading-tight">Carpenters wanting higher income</p>
             </div>
             <div className="flex items-center gap-4 bg-white dark:bg-[#111] p-6 border border-gray-200 dark:border-white/5 rounded-[1.5rem] shadow-sm">
               <Briefcase className="w-8 h-8 text-[#A67C52] shrink-0"/>
               <p className="font-bold text-lg leading-tight">Freelancers building a portfolio</p>
             </div>
          </div>
        </div>
      </section>

      {/* 4. PROBLEM → SOLUTION */}
      <section id="solution" className="py-24 bg-white dark:bg-[#0A0A0A] border-y border-gray-200 dark:border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#2D2D2D] dark:text-white">YouTube Can’t Make You a Professional</h2>
            <p className="text-xl text-gray-500 mt-4 font-light">Stop wasting time on scattered videos. Learn the right way.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-0 border border-gray-200 dark:border-white/10 rounded-[2rem] overflow-hidden shadow-xl">
            <div className="bg-[#FDFBF7] dark:bg-[#111] p-10 md:p-16 border-b md:border-b-0 md:border-r border-gray-200 dark:border-white/10">
              <div className="flex items-center gap-3 mb-8"><X className="w-8 h-8 text-red-500"/><h3 className="text-3xl font-serif font-bold text-[#2D2D2D] dark:text-white">The Old Way</h3></div>
              <ul className="space-y-6 text-lg text-gray-600 dark:text-gray-400 font-medium">
                <li className="flex items-center"><span className="w-2 h-2 bg-red-500 rounded-full mr-4"/> Random tutorials</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-red-500 rounded-full mr-4"/> No structure</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-red-500 rounded-full mr-4"/> No feedback</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-red-500 rounded-full mr-4"/> No certification</li>
              </ul>
            </div>
            <div className="bg-[#A67C52] p-10 md:p-16 text-white transform md:scale-[1.02] shadow-2xl z-10 rounded-[2rem]">
              <div className="flex items-center gap-3 mb-8"><CheckCircle2 className="w-8 h-8 text-white"/><h3 className="text-3xl font-serif font-bold">The Thachari Way</h3></div>
              <ul className="space-y-6 text-lg font-bold">
                <li className="flex items-center"><CheckCircle2 className="w-6 h-6 mr-4"/> Step-by-step curriculum</li>
                <li className="flex items-center"><CheckCircle2 className="w-6 h-6 mr-4"/> Real projects</li>
                <li className="flex items-center"><CheckCircle2 className="w-6 h-6 mr-4"/> Instructor validation</li>
                <li className="flex items-center"><CheckCircle2 className="w-6 h-6 mr-4"/> Verifiable Certificates</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. TURN SKILLS INTO INCOME */}
      <section id="income" className="py-24 bg-[#111] text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#A67C52]/10 blur-[100px] -z-10" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center z-10 relative">
           <div>
             <h2 className="text-5xl font-serif font-bold mb-6">Turn Skills Into Income.</h2>
             <p className="text-xl text-gray-300 font-light mb-10">This is not just learning — it’s a complete career upgrade. We teach you exactly what clients pay for.</p>
             <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="bg-[#A67C52] p-3 rounded-xl"><TrendingUp className="w-6 h-6 text-white"/></div>
                  <div><h4 className="text-xl font-bold mb-1">Start taking freelance interior clients</h4><p className="text-gray-400">Learn the exact workflow to pitch and deliver commercial spaces.</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#A67C52] p-3 rounded-xl"><Hammer className="w-6 h-6 text-white"/></div>
                  <div><h4 className="text-xl font-bold mb-1">Build custom furniture for customers</h4><p className="text-gray-400">Master advanced joinery to command premium prices.</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#A67C52] p-3 rounded-xl"><Briefcase className="w-6 h-6 text-white"/></div>
                  <div><h4 className="text-xl font-bold mb-1">Create a professional portfolio</h4><p className="text-gray-400">Use your approved course projects to win real-world bids.</p></div>
                </div>
             </div>
           </div>
           <div className="relative">
             <img src={IMAGES.clean} alt="Professional Architecture" className="w-full h-auto rounded-3xl border border-white/10"/>
             <div className="absolute -bottom-6 -left-6 bg-white text-black p-6 border border-gray-200 flex items-center gap-4 shadow-2xl rounded-2xl">
               <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center"><TrendingUp className="w-8 h-8 text-green-600"/></div>
               <div><p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Earning Potential</p><p className="text-2xl font-bold">Increased</p></div>
             </div>
           </div>
        </div>
      </section>

      {/* 6. COURSE PREVIEW */}
      <section id="preview" className="py-24 bg-[#FDFBF7] dark:bg-[#050505]">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-10 text-[#2D2D2D] dark:text-white">See What You'll Learn</h2>
          <div
            onClick={() => { window.location.href = 'https://thachari.com/courses' }}
            onTouchStart={() => { window.location.href = 'https://thachari.com/courses' }}
            className="relative aspect-video bg-black rounded-3xl overflow-hidden border border-gray-200 dark:border-white/10 group cursor-pointer shadow-xl mx-auto"
          >
            <img src={IMAGES.preview} className="w-full h-full object-cover opacity-70 group-hover:opacity-50 transition-opacity duration-500 group-hover:scale-105" alt="Course Preview" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center pl-2 group-hover:bg-[#A67C52] transition-colors shadow-2xl border border-white/30">
                <Play className="w-10 h-10 text-white fill-white" />
              </div>
            </div>
            <div className="absolute bottom-6 left-6 flex items-center gap-3">
              <Badge className="bg-black/80 text-white border-0 px-3 py-1 font-bold">Trailer</Badge>
              <span className="text-white font-bold text-xl drop-shadow-md">Advanced Joinery Masterclass</span>
            </div>
          </div>
          <p className="mt-8 text-xl text-gray-600 dark:text-gray-400 font-light">
            Watch how we teach real interior and carpentry skills step-by-step.
          </p>
        </div>
      </section>

      {/* 7. METHODOLOGY */}
      <section id="methodology" className="py-32 bg-white dark:bg-[#0A0A0A] border-y border-gray-200 dark:border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-[#A67C52] font-bold tracking-[0.2em] uppercase text-xs mb-4">The Methodology</h2>
          <h3 className="text-5xl font-serif font-bold mb-20 text-[#2D2D2D] dark:text-white">How You Become a Pro.</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 relative">
            <div className="hidden md:block absolute top-10 left-[10%] right-[10%] h-[1px] bg-gray-200 dark:bg-white/10 z-0" />
            {[
              { icon: Play, title: "Watch", desc: "Structured lessons" },
              { icon: Download, title: "Download", desc: "Plans & materials" },
              { icon: BrainCircuit, title: "Pass", desc: "Interactive quizzes" },
              { icon: Hammer, title: "Build", desc: "Real projects" },
              { icon: ShieldCheck, title: "Certify", desc: "Industry recognition" }
            ].map((step, i) => (
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} key={i} className="relative z-10 flex flex-col items-center">
                <div className="w-20 h-20 bg-[#FDFBF7] dark:bg-[#111] border border-gray-200 dark:border-white/10 rounded-[1.5rem] flex items-center justify-center mb-6 shadow-sm">
                  <step.icon className="w-8 h-8 text-[#A67C52]" />
                </div>
                <h4 className="text-2xl font-bold mb-2 text-[#2D2D2D] dark:text-white">{step.title}</h4>
                <p className="text-gray-500 font-medium">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. CURRICULUM DIFFERENTIATORS */}
      <section id="curriculum" className="py-32 bg-[#111] text-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-serif font-bold mb-6">Not Just Learning — Real Skill Building</h2>
            <p className="text-xl text-gray-400 font-light">Everything you need to launch your career or upgrade your business.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-[#1A1A1A] p-10 border border-white/5 hover:border-[#A67C52]/50 transition-colors rounded-3xl">
              <Play className="w-10 h-10 text-[#A67C52] mb-6"/>
              <h3 className="text-2xl font-bold mb-4">Cinema-Quality Lessons</h3>
              <p className="text-gray-400 font-light">Crisp 4K multi-part video sequences that show you every detail, joint, and measurement clearly.</p>
            </div>
            <div className="bg-[#1A1A1A] p-10 border border-white/5 hover:border-[#A67C52]/50 transition-colors rounded-3xl">
              <BrainCircuit className="w-10 h-10 text-[#A67C52] mb-6"/>
              <h3 className="text-2xl font-bold mb-4">Your Personal AI Coach</h3>
              <p className="text-gray-400 font-light">Stuck on an idea? Use our integrated Gemini AI to generate spatial layouts and color palettes instantly.</p>
            </div>
            <div className="bg-[#1A1A1A] p-10 border border-white/5 hover:border-[#A67C52]/50 transition-colors rounded-3xl">
              <ShieldCheck className="w-10 h-10 text-[#A67C52] mb-6"/>
              <h3 className="text-2xl font-bold mb-4">Verifiable Certificates</h3>
              <p className="text-gray-400 font-light">Earn dynamic, professional PDF certificates to show clients and employers you know your craft.</p>
            </div>
            <div className="bg-[#1A1A1A] p-10 border border-white/5 hover:border-[#A67C52]/50 transition-colors rounded-3xl">
              <Smartphone className="w-10 h-10 text-[#A67C52] mb-6"/>
              <h3 className="text-2xl font-bold mb-4">Learn on Any Device</h3>
              <p className="text-gray-400 font-light">Sync across 3 devices. Download videos on our mobile app for offline viewing in the workshop.</p>
            </div>
            <div className="bg-[#1A1A1A] p-10 border border-white/5 hover:border-[#A67C52]/50 transition-colors lg:col-span-2 rounded-3xl">
              <Globe className="w-10 h-10 text-[#A67C52] mb-6"/>
              <h3 className="text-2xl font-bold mb-4">Multi-Language Support</h3>
              <p className="text-gray-400 font-light">Learn in your mother tongue. The entire platform natively translates into 10 languages including Hindi, Tamil, Spanish, and Arabic.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. STUDENT RESULTS */}
      <section id="results" className="py-32 bg-white dark:bg-[#0A0A0A] border-b border-gray-200 dark:border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
             <div className="flex justify-center gap-2 text-yellow-500 mb-6">
               {[1,2,3,4,5].map(i => <Star key={i} className="w-8 h-8 fill-current"/>)}
             </div>
             <h2 className="text-5xl font-serif font-bold text-[#2D2D2D] dark:text-white">Real Results from Students</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { name: "Arjun K.", role: "Freelance Designer", text: "Got my first ₹25,000 interior project in 7 days after finishing the commercial zoning module. The blueprints gave me the confidence to pitch." },
              { name: "Michael R.", role: "Carpenter", text: "I used to just do basic framing. I now take high-end custom furniture orders from clients directly because I learned advanced joinery here." },
              { name: "Priya S.", role: "Interior Architect", text: "Now working as a freelance designer full-time earning ₹60K/month. The AI Design Assistant saves me hours on client layouts, and the certificate helped me secure my first retainer." }
            ].map((review, i) => (
              <div key={i} className="bg-[#FDFBF7] dark:bg-[#111] p-10 border border-gray-200 dark:border-white/5 rounded-3xl shadow-sm">
                <Quote className="w-10 h-10 text-[#A67C52] mb-6 opacity-40"/>
                <p className="text-lg text-[#2D2D2D] dark:text-gray-300 mb-8 font-bold leading-relaxed">"{review.text}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white dark:bg-[#222] rounded-full flex items-center justify-center font-serif text-xl border border-gray-200 dark:border-white/10 text-[#2D2D2D] dark:text-white">{review.name.charAt(0)}</div>
                  <div>
                    <p className="font-bold text-[#2D2D2D] dark:text-white">{review.name}</p>
                    <p className="text-xs text-[#A67C52] font-bold uppercase tracking-wider">{review.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              onClick={() => { window.location.href = 'https://thachari.com/signup' }}
              onTouchStart={() => { window.location.href = 'https://thachari.com/signup' }}
              className="h-16 px-12 text-lg font-bold bg-[#2D2D2D] dark:bg-white text-white dark:text-[#2D2D2D] hover:bg-[#A67C52] hover:text-white rounded-none transition-colors duration-300"
            >
              Start Learning Now <ArrowRight className="ml-2 w-5 h-5"/>
            </Button>
          </div>
        </div>
      </section>

      {/* 10. PRICING */}
      <section id="pricing" className="py-24 px-6 lg:px-12 bg-[#FDFBF7] dark:bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-12 flex flex-col items-center">
            <h3 className="text-5xl font-serif font-bold mb-6 text-[#2D2D2D] dark:text-white">
              {pricingSettings?.subscriptions?.enabled ? 'Choose Your Plan' : 'One Plan. Premium Access.'}
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-xl">
              {pricingSettings?.subscriptions?.enabled
                ? 'Full access to all courses, certificates and the mobile app. Pay for 1, 3, 6 or 12 months — no auto-renewal — or once for lifetime access.'
                : 'Pay once, learn forever. Every course, every future update, no recurring fees.'}
            </p>
          </div>

          <AdmissionCounter />

          {/* Same card, plans and prices as /pricing (components/PricingPlanCard.jsx) */}
          <div className="max-w-[480px] mx-auto mt-12 relative">
            <HomePricingCard settings={pricingSettings} />
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-16 mt-12 text-[#2D2D2D] dark:text-white font-bold text-lg">
             <div className="flex items-center"><RefreshCcw className="w-6 h-6 mr-3 text-[#A67C52]"/> 7-Day Refund Guarantee</div>
             <div className="flex items-center"><CreditCard className="w-6 h-6 mr-3 text-[#A67C52]"/> Secure Payments (Stripe/PayPal)</div>
             <div className="flex items-center"><ShieldCheck className="w-6 h-6 mr-3 text-[#A67C52]"/> No Hidden Fees</div>
          </div>
        </div>
      </section>

      {/* 11. FAQ */}
      <section id="faq" className="py-24 bg-white dark:bg-[#050505] border-t border-gray-200 dark:border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-serif font-bold text-[#2D2D2D] dark:text-white">Frequently Asked Questions</h3>
          </div>
          <div className="space-y-3">
            {faqList.map((faq, i) => (
              <div key={i} className="border border-gray-200 dark:border-white/10 p-6 bg-[#FDFBF7] dark:bg-[#111] rounded-2xl cursor-pointer transition-colors hover:border-[#A67C52]/50" onClick={() => setActiveFaq(activeFaq === i ? null : i)}>
                <div className="flex justify-between items-center font-bold text-lg text-[#2D2D2D] dark:text-white">
                  {faq.q}
                  <span className="text-[#A67C52] text-2xl font-light leading-none">{activeFaq === i ? '-' : '+'}</span>
                </div>
                <AnimatePresence>
                  {activeFaq === i && (
                    <motion.p 
                      initial={{ height: 0, opacity: 0, marginTop: 0 }}
                      animate={{ height: "auto", opacity: 1, marginTop: 16 }}
                      exit={{ height: 0, opacity: 0, marginTop: 0 }}
                      className="text-gray-600 dark:text-gray-400 font-medium overflow-hidden"
                    >
                      {faq.a}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 12. CONTACT US */}
      <section id="contact" className="py-24 bg-[#FDFBF7] dark:bg-[#0A0A0A] border-t border-gray-200 dark:border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-20 items-center">
          <div>
            <h2 className="text-[#A67C52] font-bold tracking-[0.2em] uppercase text-xs mb-4">Support & Inquiries</h2>
            <h3 className="text-5xl font-serif font-bold mb-8 text-[#2D2D2D] dark:text-white">Get in Touch.</h3>
            <p className="text-xl text-gray-600 dark:text-gray-400 font-light mb-12 max-w-lg">Whether you need help with a course, have billing questions, or want to inquire about enterprise accounts for your firm.</p>
            <div className="space-y-6 text-lg font-medium text-[#2D2D2D] dark:text-white">
              <div className="flex items-center"><Mail className="w-6 h-6 text-[#A67C52] mr-4"/> support@thachari.com</div>
              <div className="flex items-center"><MapPin className="w-6 h-6 text-[#A67C52] mr-4"/> 100 Artisan Way, Design District</div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center"><Phone className="w-6 h-6 text-[#A67C52] mr-4"/>
                  <div className="flex flex-col text-base font-semibold">
                    <a href="tel:+919626722220" className="hover:text-[#A67C52] transition-colors">+91 96267 22220</a>
                    <a href="tel:+919626822220" className="hover:text-[#A67C52] transition-colors">+91 96268 22220</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-10">
                  <a href="https://wa.me/919626722220" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-4 py-2 text-sm font-bold hover:opacity-90 transition-opacity">
                    <WhatsAppIcon className="w-4 h-4" /> 96267 22220
                  </a>
                  <a href="https://wa.me/919626822220" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-4 py-2 text-sm font-bold hover:opacity-90 transition-opacity">
                    <WhatsAppIcon className="w-4 h-4" /> 96268 22220
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-[#111] p-10 border border-gray-200 dark:border-white/10 rounded-[2rem] shadow-xl">
            <form className="space-y-6 text-[#2D2D2D] dark:text-white">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div><label className="block text-sm mb-2 font-bold">First Name</label><input type="text" className="w-full bg-[#FDFBF7] dark:bg-[#0A0A0A] border border-gray-300 dark:border-white/10 rounded-xl p-4 focus:outline-none focus:border-[#A67C52] transition-colors" /></div>
                <div><label className="block text-sm mb-2 font-bold">Last Name</label><input type="text" className="w-full bg-[#FDFBF7] dark:bg-[#0A0A0A] border border-gray-300 dark:border-white/10 rounded-xl p-4 focus:outline-none focus:border-[#A67C52] transition-colors" /></div>
              </div>
              <div><label className="block text-sm mb-2 font-bold">Email Address</label><input type="email" className="w-full bg-[#FDFBF7] dark:bg-[#0A0A0A] border border-gray-300 dark:border-white/10 rounded-xl p-4 focus:outline-none focus:border-[#A67C52] transition-colors" /></div>
              <div><label className="block text-sm mb-2 font-bold">Message</label><textarea rows="4" className="w-full bg-[#FDFBF7] dark:bg-[#0A0A0A] border border-gray-300 dark:border-white/10 rounded-xl p-4 focus:outline-none focus:border-[#A67C52] transition-colors"></textarea></div>
              <Button type="button" className="w-full h-14 rounded-xl bg-[#A67C52] hover:bg-[#8B6640] text-white text-lg font-bold shadow-md transition-transform hover:-translate-y-1">Send Message</Button>
            </form>
          </div>
        </div>
      </section>

      {/* 13. FINAL CTA (AGGRESSIVE) */}
      <section className="py-32 bg-[#A67C52] text-white text-center border-t border-white/10">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <h2 className="text-5xl md:text-6xl font-serif font-bold mb-6 text-white drop-shadow-md">Start Building Real Skills Today</h2>
          <p className="text-xl font-bold text-black bg-white/20 inline-block px-6 py-2 mb-12 uppercase tracking-widest rounded-md">Join thousands already learning and earning.</p>
          <div className="flex justify-center">
            <Button
              onClick={() => { window.location.href = 'https://thachari.com/signup' }}
              onTouchStart={() => { window.location.href = 'https://thachari.com/signup' }}
              className="h-20 px-12 md:px-16 text-xl md:text-2xl font-bold bg-black text-white hover:bg-gray-900 rounded-none shadow-lg transition-transform hover:-translate-y-1"
            >
              Start Learning Now <ArrowRight className="ml-3 w-6 h-6 md:w-8 md:h-8"/>
            </Button>
          </div>
        </div>
      </section>

      {/* MEGA FOOTER */}
      <footer className="bg-[#050505] text-white pt-24 pb-24 md:pb-12">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12 mb-20 border-b border-white/10 pb-20">
            {/* Brand Column */}
            <div className="col-span-2">
              <h2 className="text-3xl font-serif font-bold mb-6 text-[#A67C52]">THACHARI</h2>
              <p className="text-gray-400 font-medium max-w-sm mb-8">The ultimate skill transformation system for interior design and carpentry. Build skills. Get jobs. Earn money.</p>
              <div className="flex items-center gap-4 text-gray-500">
                <Globe className="w-5 h-5"/> <span className="font-bold uppercase tracking-widest text-xs">Global Translation Active</span>
              </div>
            </div>
            
            {/* Product Column */}
            <div>
              <h4 className="font-bold mb-6 tracking-widest text-sm uppercase text-white">Product</h4>
              <ul className="space-y-4 text-gray-400 font-medium text-sm">
                <li><a href="#features" onClick={(e) => handleScroll(e, 'features')} className="hover:text-white transition-colors cursor-pointer">Platform Features</a></li>
                <li><a href="#pricing" onClick={(e) => handleScroll(e, 'pricing')} className="hover:text-white transition-colors cursor-pointer">Pricing & Value</a></li>
                <li><a href="#methodology" onClick={(e) => handleScroll(e, 'methodology')} className="hover:text-white transition-colors cursor-pointer">How It Works</a></li>
                <li><a href="#certification" onClick={(e) => handleScroll(e, 'certification')} className="hover:text-white transition-colors cursor-pointer">Verifiable Certificates</a></li>
                <li><Link to="/features/ai" className="hover:text-white transition-colors flex items-center gap-2">Gemini AI Assistant <Badge className="bg-[#A67C52] text-[10px] px-1 py-0 h-4 border-0">NEW</Badge></Link></li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h4 className="font-bold mb-6 tracking-widest text-sm uppercase text-white">Resources</h4>
              <ul className="space-y-4 text-gray-400 font-medium text-sm">
                <li><Link to="/courses" className="hover:text-white transition-colors">Course Catalog</Link></li>
                <li><a href="https://thachari.com/courses" className="hover:text-white transition-colors">Free Lesson Preview</a></li>
                <li><a href="#results" onClick={(e) => handleScroll(e, 'results')} className="hover:text-white transition-colors cursor-pointer">Student Results</a></li>
                <li><a href="#community" onClick={(e) => handleScroll(e, 'community')} className="hover:text-white transition-colors cursor-pointer">Project Guild</a></li>
                <li><a href="#faq" onClick={(e) => handleScroll(e, 'faq')} className="hover:text-white transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>

            {/* Company & Legal Column */}
            <div>
              <h4 className="font-bold mb-6 tracking-widest text-sm uppercase text-white">Company</h4>
              <ul className="space-y-4 text-gray-400 font-medium text-sm">
                <li><a href="#contact" onClick={(e) => handleScroll(e, 'contact')} className="hover:text-white transition-colors cursor-pointer">Contact Us</a></li>
                <li><Link to="/instructors" className="hover:text-white transition-colors">Master Faculty</Link></li>
                <li><Link to="/affiliates" className="hover:text-white transition-colors">Affiliate Program</Link></li>
                <li className="pt-4 mt-4 border-t border-white/10"><Link to="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link></li>
                <li><Link to="/refund-policy" className="hover:text-white transition-colors">Refund &amp; Cancellation Policy</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="text-center text-sm font-medium text-gray-600">
            <p>© {new Date().getFullYear()} Thachari Education. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* MOBILE STICKY CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-[#09090b]/90 backdrop-blur-md border-t border-gray-200 dark:border-white/10 p-4 flex justify-between items-center z-[60] md:hidden shadow-[0_-10px_20px_rgba(0,0,0,0.05)]">
        <span className="font-bold text-sm text-[#2D2D2D] dark:text-white">Start Learning Today</span>
        <Button
          onClick={() => { window.location.href = 'https://thachari.com/courses' }}
          onTouchStart={() => { window.location.href = 'https://thachari.com/courses' }}
          className="bg-[#A67C52] hover:bg-[#8B6640] text-white rounded-md h-12 px-6 font-bold text-sm shadow-md"
        >
          Start Free Lesson
        </Button>
      </div>

    </div>
  );
}